'use client';

import { useEffect, useRef } from 'react';
import { ddRumTimingOnce } from '@/utils/datadog';

/**
 * Fires `ddRumTimingOnce(name)` exactly once — after the first frame is painted
 * once `ready` is true. Two chained `requestAnimationFrame` calls cross a paint
 * boundary, so the timing reflects user-perceived render
 * rather than React commit.
 *
 * `hasFired` is set only when the timing is actually emitted (inside the inner
 * frame), so a cancelled frame — React Strict Mode's setup/cleanup cycle, or
 * `ready` toggling — reschedules instead of being permanently suppressed.
 * `ddRumTimingOnce` additionally dedupes per RUM view (reset on route change).
 *
 * @param name  RUM view custom-timing name.
 * @param ready When it first flips true, the timing is scheduled for the next painted frame.
 */
export const useRenderTiming = (name: string, ready: boolean) => {
    const hasFired = useRef(false);

    useEffect(() => {
        if (!ready || hasFired.current) {
            return;
        }

        let innerRaf = 0;
        const outerRaf = requestAnimationFrame(() => {
            innerRaf = requestAnimationFrame(() => {
                hasFired.current = true;
                ddRumTimingOnce(name);
            });
        });

        return () => {
            cancelAnimationFrame(outerRaf);
            cancelAnimationFrame(innerRaf);
        };
    }, [name, ready]);
};
